import Modal from "@Components/Modal";
import TenanciesSection from "../TenanciesSection";
import styles from "./viewMoreDetailsModal.module.scss";
import RoomFeaturedImages from "./components/RoomFeaturedImages/RoomFeaturedImages";
import Description from "./components/Description";
import Amenities from "./components/Amenities";
import throttle from "@Utils/throttle";
import { UIEventHandler, useRef, useState, memo, useEffect } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import Portal from "@Components/Portal";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import Footer from "./components/Footer";
import ArrowUpIcon from "@Icons/ArrowUpIcon";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useAnalytics } from "helpers/analytics";
import { PAGE } from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import useWhitelabel from "hooks/useWhitelabel";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "@Store/index";
import axios from "axios";
import config from "@Config/index";
import { getRatingLabel } from "@Pages/PropertyPage/components/ReviewsSectionV2/buildReviewsData";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";

type ViewMoreDetailsModalProps = {
  heading: string;
  // tenancies: any;
  roomTypeData: any;
  isSoldOut?: boolean;
  onClose: any;
  assetsLength: any;
  images: any;
  features: any;
  featuredImage: any;
  description: any;
  virtualView: any;
  availableFrom?: any;
  isFlexible?: boolean;
  shouldModalScroll?: any;
  hideFooter?: boolean;
  scrollToAmenities?: boolean;
};

const ViewMoreDetailsModal = ({
  heading,
  onClose,
  // tenancies,
  roomTypeData,
  isSoldOut,
  assetsLength,
  images,
  features,
  featuredImage,
  description,
  virtualView,
  availableFrom = "",
  isFlexible = false,
  shouldModalScroll = false,
  hideFooter = false,
  scrollToAmenities = false,
}: ViewMoreDetailsModalProps) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const [showMoreTenancyButton, setShowMoreTenancyButton] = useState<boolean>(true);
  const [tenancies, setTenancies] = useState([]);
  const { isWhitelabel, whitelabelColors } = useWhitelabel();
  const {
    lastScreen,
    propertyData,
    userCurrency,
    reviewsRating: rawReviewsRating,
  } = useSelector(({ propertyPageData, appData }: AppState) => ({
    lastScreen: appData.lastScreen,
    propertyData: propertyPageData,
    userCurrency: appData.userCurrency,
    reviewsRating: propertyPageData?.data?.reviews_rating,
  }));

  const reviewsRating = rawReviewsRating ? parseFloat(String(rawReviewsRating)) : 0;

  const modalPanelRef = useRef<HTMLDivElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationHook();
  const ratingLabel = t(getRatingLabel(reviewsRating));
  const isReviewsSectionV2Enabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.REVIEWS_SECTION_V2) || config.IS_CHINA_APP;
  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;
  const floorPlanImages =
    roomTypeData?.images?.filter((image: any) => image.type === "floor_plan") || [];
  const { trackTenancyView } = useAnalytics();

  const handleFloorPlanClick = () => {
    gtagEventEmitter({
      action: "room-type-floor-plan-clicked",
      category: "room-type",
      label: "floor_plan_modal",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: floorPlanImages,
      category: "floor-plan",
      currentItemIndex: 0,
      showViewRoomButton: false,
      config_id: roomTypeData.id,
    });
  };

  useEffect(() => {
    trackTenancyView(String(roomTypeData?.id), heading);
  }, []);

  const scrollToBottom = () => {
    if (modalPanelRef && modalPanelRef.current) {
      modalPanelRef.current.scroll({
        top: modalPanelRef.current.offsetHeight,
        left: 0,
        behavior: "smooth",
      });
    }
    gtagEventEmitter({
      action: "choose-tenancy-pill",
      category: PAGE.INVENTORY,
      label: "view_more_details",
    });
  };

  const scrollToAmenitiesSection = () => {
    if (amenitiesRef && amenitiesRef.current && modalPanelRef && modalPanelRef.current) {
      const amenitiesOffset = amenitiesRef.current.offsetTop;
      modalPanelRef.current.scroll({
        top: amenitiesOffset - 56, // Adding 56px padding from top
        left: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    logAppEvent({
      eventName: APP_EVENTS.PAGE_VIEWED,
      payload: {
        screen: APP_PAGE.ROOM_TYPE_DETAILS,
        previous_screen: lastScreen,
        canonical_name: propertyData?.canonical_name,
        property_id: propertyData?.data?.id,
        property_name: propertyData?.data?.name,
        region_id: propertyData?.data?.region_id,
      },
    });
  }, []);

  useEffect(() => {
    if (shouldModalScroll) scrollToBottom();
  }, [shouldModalScroll]);

  useEffect(() => {
    if (scrollToAmenities) {
      // Small delay to ensure modal is fully rendered
      setTimeout(scrollToAmenitiesSection, 100);
    }
  }, [scrollToAmenities]);

  const fetchAllTenancies = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/v0/inventories/${roomTypeData.id}`, {
        params: {
          type: "empty",
          only: "id",
          methods: "active_children",
          user_currency: userCurrency,
          use_java_api: true,
        },
        headers: { "X-Use-Java-Api": "true" },
      });
      setTenancies(response?.data?.data?.active_children || []);
    } catch (error) {
      console.error("Error fetching tenancies:", error);
      setTenancies([]);
    }
  };

  useEffect(() => {
    fetchAllTenancies();
  }, []);

  const handleModalScroll: UIEventHandler = throttle(() => {
    if (modalPanelRef && modalPanelRef.current && showMoreTenancyButton) {
      const observer = new IntersectionObserver((entries) => {
        const element = entries[0];
        if (element.isIntersecting) {
          setShowMoreTenancyButton(false);
          observer.unobserve(element.target);
        }
      });
      observer.observe(modalPanelRef.current);
    }
  }, 300);

  return (
    <Portal rootId="forPortal">
      <Modal
        modalRef={modalPanelRef}
        hideModal={onClose}
        size={isDesktop ? "medium" : "fullScreen"}
        animation={isDesktop ? "opacity" : "bottomToTop"}
        className={formatClassNames(styles.container, isDesktop && styles.desktop)}
        zIndex={1000}
        onScroll={handleModalScroll}
        align="center"
        type="ViewMoreDetailsModal"
        title={heading}
        closeBtnClassName={formatClassNames(styles.closeBtn, isDesktop && styles.desktop)}
        isHistoryPush={!isDesktop}
      >
        <div>
          {/* Sold out message */}
          {/* {isSoldOut && <SoldOutInfo />} */}
          {!isDesktop && (
            <RoomFeaturedImages
              images={images}
              assetsLength={assetsLength}
              isDesktop={isDesktop}
              virtualView={virtualView}
            />
          )}
          <div className={formatClassNames(styles.modalBody, isDesktop && styles.desktop)}>
            {/* Gallery */}
            {isDesktop && (
              <RoomFeaturedImages
                images={images}
                assetsLength={assetsLength}
                isDesktop={isDesktop}
                virtualView={virtualView}
              />
            )}
            {/* Rating + availability row */}
            {isReviewsSectionV2Enabled ? (
              <div className={styles.ratingRow}>
                {reviewsRating > 0 && (
                  <>
                    <div className={styles.ratingBadge}>
                      <span className={styles.ratingValue}>{reviewsRating.toFixed(1)}</span>
                      <span className={styles.ratingMax}>/5</span>
                    </div>
                    {ratingLabel && <span className={styles.ratingLabel}>{t(ratingLabel)}</span>}
                  </>
                )}
                {reviewsRating > 0 && !isDesktop && <div className={styles.mobileLineBreak} />}
                {!isSoldOut && (availableFrom || isFlexible) && (
                  <>
                    {reviewsRating > 0 && isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span className={styles.availableTag}>
                      {isFlexible
                        ? t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")
                        : `${t(
                            "propertypage.roomtypesectiondynamic.roomtypecard.availablefrom",
                          )}: ${availableFrom}`}
                    </span>
                  </>
                )}
                {isSoldOut && (
                  <>
                    {reviewsRating > 0 && isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span className={styles.soldOutBadge}>
                      {t("propertypage.roomtypesectiondynamic.soldout") || "Sold Out"}
                    </span>
                  </>
                )}
                {isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
                  <>
                    {!isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span
                      className={styles.viewFloorPlanLink}
                      onClick={handleFloorPlanClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                      }}
                      data-testid="modal-view-floor-plan"
                    >
                      <FloorPlanIcon size="16" />
                      <span>
                        {t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}
                      </span>
                    </span>
                  </>
                )}
              </div>
            ) : (
              <></>
            )}
            {/* Description Text */}
            <Description content={description} />
          </div>
          {/* Amenities */}
          <div ref={amenitiesRef}>
            <Amenities features={features} isDesktop={isDesktop} />
          </div>

          {/* Tenancies */}
          <TenanciesSection
            tenancies={tenancies}
            roomTypeData={roomTypeData}
            isSoldOut={Boolean(isSoldOut)}
            featuredImage={featuredImage}
            isInsideModal
            isDesktop={isDesktop}
          />
          {!isDesktop && (
            <>
              <div className={styles.divider} />
              <div
                className={formatClassNames(
                  styles.modalBody,
                  isDesktop && styles.desktop,
                  styles.noBorder,
                )}
              >
                {!hideFooter && (
                  <Footer
                    type={roomTypeData?.meta?.unit_type}
                    id={roomTypeData?.id}
                    onClose={onClose}
                  />
                )}
              </div>
            </>
          )}
          {showMoreTenancyButton && (
            <div
              className={formatClassNames(styles.moreTenanciesPill, isDesktop && styles.desktop)}
            >
              <button type="button" onClick={scrollToBottom}>
                {t("propertypage.roomtypesectiondynamic.roomtypecard.chooseyourtenancy")}
                <ArrowUpIcon {...(isWhitelabel && { fill: whitelabelColors?.cta_color })} />
              </button>
            </div>
          )}
        </div>
      </Modal>
    </Portal>
  );
};

export default wrapperHOC(memo(ViewMoreDetailsModal), {
  componentName: "ViewMoreDetailsModal-RoomTypeCard-PropertyPage",
  showForChina: true,
});
